import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import type {
  AssistDecisionTree,
  AssistDecisionNode,
  AssistDecisionResult,
  GuidedAnalysisFlow,
  IntrospectionFlow,
} from "../modules/assist/models";

export interface ActiveDecisionTreeState {
  treeId: string;
  currentNodeId: string;
  path: { nodeId: string; optionId: string }[];
  resultId?: string;
}

export interface ActiveGuidedAnalysisState {
  flowId: string;
  currentStepIndex: number;
  answers: Record<string, string>;
}

export interface ActiveIntrospectionState {
  flowId: string;
  currentStepIndex: number;
  answers: Record<string, string>;
}

export interface AssistState {
  activeDecisionTree?: ActiveDecisionTreeState;
  activeGuidedAnalysis?: ActiveGuidedAnalysisState;
  activeIntrospection?: ActiveIntrospectionState;
}

interface AssistContextValue {
  state: AssistState;
  startDecisionTree: (tree: AssistDecisionTree) => void;
  answerDecisionNode: (tree: AssistDecisionTree, optionId: string) => void;
  startGuidedAnalysis: (flow: GuidedAnalysisFlow) => void;
  setGuidedAnalysisAnswer: (stepId: string, answer: string) => void;
  goToNextGuidedStep: (flow: GuidedAnalysisFlow) => void;
  goToPreviousGuidedStep: (flow: GuidedAnalysisFlow) => void;
  startIntrospection: (flow: IntrospectionFlow) => void;
  setIntrospectionAnswer: (stepId: string, answer: string) => void;
  goToNextIntrospectionStep: (flow: IntrospectionFlow) => void;
  goToPreviousIntrospectionStep: (flow: IntrospectionFlow) => void;
}

const AssistContext = createContext<AssistContextValue | undefined>(undefined);

function getNode(tree: AssistDecisionTree, nodeId: string): AssistDecisionNode | undefined {
  return tree.nodes.find((n) => n.id === nodeId);
}

function getResult(tree: AssistDecisionTree, resultId: string): AssistDecisionResult | undefined {
  return tree.results.find((r) => r.id === resultId);
}

export function AssistProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AssistState>({});

  const startDecisionTree = useCallback((tree: AssistDecisionTree) => {
    setState((prev) => ({
      ...prev,
      activeDecisionTree: {
        treeId: tree.id,
        currentNodeId: tree.rootNodeId,
        path: [],
        resultId: undefined,
      },
    }));
  }, []);

  const answerDecisionNode = useCallback((tree: AssistDecisionTree, optionId: string) => {
    setState((prev) => {
      const current = prev.activeDecisionTree;
      const currentNode = current ? getNode(tree, current.currentNodeId) : undefined;
      if (!currentNode) return prev;
      const opt = currentNode.options.find((o) => o.id === optionId);
      if (!opt) return prev;
      const nextPath = [...(current?.path || []), { nodeId: currentNode.id, optionId }];
      if (opt.resultId) {
        return {
          ...prev,
          activeDecisionTree: {
            treeId: tree.id,
            currentNodeId: currentNode.id,
            path: nextPath,
            resultId: opt.resultId,
          },
        };
      }
      if (opt.nextNodeId) {
        return {
          ...prev,
          activeDecisionTree: {
            treeId: tree.id,
            currentNodeId: opt.nextNodeId,
            path: nextPath,
            resultId: undefined,
          },
        };
      }
      return prev;
    });
  }, []);

  const startGuidedAnalysis = useCallback((flow: GuidedAnalysisFlow) => {
    setState((prev) => ({
      ...prev,
      activeGuidedAnalysis: {
        flowId: flow.id,
        currentStepIndex: 0,
        answers: {},
      },
    }));
  }, []);

  const setGuidedAnalysisAnswer = useCallback((stepId: string, answer: string) => {
    setState((prev) => {
      const ga = prev.activeGuidedAnalysis;
      if (!ga) return prev;
      return {
        ...prev,
        activeGuidedAnalysis: {
          ...ga,
          answers: { ...ga.answers, [stepId]: answer },
        },
      };
    });
  }, []);

  const goToNextGuidedStep = useCallback((flow: GuidedAnalysisFlow) => {
    setState((prev) => {
      const ga = prev.activeGuidedAnalysis;
      if (!ga) return prev;
      const nextIndex = Math.min(ga.currentStepIndex + 1, flow.steps.length - 1);
      return {
        ...prev,
        activeGuidedAnalysis: {
          ...ga,
          currentStepIndex: nextIndex,
        },
      };
    });
  }, []);

  const goToPreviousGuidedStep = useCallback((flow: GuidedAnalysisFlow) => {
    setState((prev) => {
      const ga = prev.activeGuidedAnalysis;
      if (!ga) return prev;
      const nextIndex = Math.max(ga.currentStepIndex - 1, 0);
      return {
        ...prev,
        activeGuidedAnalysis: {
          ...ga,
          currentStepIndex: nextIndex,
        },
      };
    });
  }, []);

  const startIntrospection = useCallback((flow: IntrospectionFlow) => {
    setState((prev) => ({
      ...prev,
      activeIntrospection: {
        flowId: flow.id,
        currentStepIndex: 0,
        answers: {},
      },
    }));
  }, []);

  const setIntrospectionAnswer = useCallback((stepId: string, answer: string) => {
    setState((prev) => {
      const ai = prev.activeIntrospection;
      if (!ai) return prev;
      return {
        ...prev,
        activeIntrospection: {
          ...ai,
          answers: { ...ai.answers, [stepId]: answer },
        },
      };
    });
  }, []);

  const goToNextIntrospectionStep = useCallback((flow: IntrospectionFlow) => {
    setState((prev) => {
      const ai = prev.activeIntrospection;
      if (!ai) return prev;
      const nextIndex = Math.min(ai.currentStepIndex + 1, flow.steps.length - 1);
      return {
        ...prev,
        activeIntrospection: {
          ...ai,
          currentStepIndex: nextIndex,
        },
      };
    });
  }, []);

  const goToPreviousIntrospectionStep = useCallback((flow: IntrospectionFlow) => {
    setState((prev) => {
      const ai = prev.activeIntrospection;
      if (!ai) return prev;
      const nextIndex = Math.max(ai.currentStepIndex - 1, 0);
      return {
        ...prev,
        activeIntrospection: {
          ...ai,
          currentStepIndex: nextIndex,
        },
      };
    });
  }, []);

  const value = useMemo<AssistContextValue>(
    () => ({
      state,
      startDecisionTree,
      answerDecisionNode,
      startGuidedAnalysis,
      setGuidedAnalysisAnswer,
      goToNextGuidedStep,
      goToPreviousGuidedStep,
      startIntrospection,
      setIntrospectionAnswer,
      goToNextIntrospectionStep,
      goToPreviousIntrospectionStep,
    }),
    [
      state,
      startDecisionTree,
      answerDecisionNode,
      startGuidedAnalysis,
      setGuidedAnalysisAnswer,
      goToNextGuidedStep,
      goToPreviousGuidedStep,
      startIntrospection,
      setIntrospectionAnswer,
      goToNextIntrospectionStep,
      goToPreviousIntrospectionStep,
    ]
  );

  return <AssistContext.Provider value={value}>{children}</AssistContext.Provider>;
}

export function useAssist() {
  const ctx = useContext(AssistContext);
  if (!ctx) throw new Error("useAssist must be used within AssistProvider");
  return ctx;
}
